// Account support for platform administrators (FR-70, FR-09b): find an
// individual by email or phone (matched by HMAC on the server, shown masked),
// then suspend with an admin hold, reactivate or re-verify their identity.
// Every action takes a reason code; free text is never stored.
import {
  Badge,
  Button,
  Group,
  NativeSelect,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { useStepUp } from "../../shared/session/stepup";
import { AdminLayout } from "./AdminLayout";
import { useRequested } from "./approvals";

type Account = Schemas["AccountSummary"];
type Reason = Schemas["AdminReason"];
type By = "email" | "phone";

export const REASONS: Reason[] = [
  "fraud_report",
  "identity_dispute",
  "legal_request",
  "name_change",
  "individual_request",
  "resolved",
  "other",
];

export function AdminAccountsPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_accounts.intro")}>
      <Accounts />
    </AdminLayout>
  );
}

function Accounts() {
  const { t } = useTranslation();
  const [by, setBy] = useState<By>("email");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Account[] | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { data, error } = await api.GET("/admin/accounts", {
      params: { query: by === "email" ? { email: value.trim() } : { phone: value.trim() } },
    });
    setBusy(false);
    if (!data) {
      setResults(null);
      return setError(errorMessage(error, t("error.generic")));
    }
    setResults(data.items);
  };

  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md">
        <form onSubmit={search} data-testid="account-search">
          <Stack gap="sm">
            <Stack gap={4}>
              <Text size="sm" fw={500} id="account-search-by">
                {t("admin_accounts.by")}
              </Text>
              <SegmentedControl
                aria-labelledby="account-search-by"
                w="fit-content"
                value={by}
                onChange={(v) => {
                  setBy(v as By);
                  setValue("");
                }}
                data={[
                  { value: "email", label: t("admin_accounts.by_email") },
                  { value: "phone", label: t("admin_accounts.by_phone") },
                ]}
              />
            </Stack>
            <TextInput
              type={by === "email" ? "email" : "tel"}
              label={by === "email" ? t("admin_accounts.email") : t("admin_accounts.phone")}
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
              required
              autoComplete="off"
              spellCheck={false}
              maxLength={by === "email" ? 254 : 20}
            />
            <FormError message={error} />
            <Button type="submit" loading={busy} w="fit-content">
              {t("admin_accounts.search")}
            </Button>
          </Stack>
        </form>
      </Paper>
      {results !== null &&
        (results.length === 0 ? (
          <Text c="dimmed" data-testid="accounts-empty">
            {t("admin_accounts.none")}
          </Text>
        ) : (
          results.map((a) => (
            <AccountCard
              key={a.account_id}
              account={a}
              onChange={(next) =>
                setResults((rs) => rs?.map((r) => (r.account_id === next.account_id ? next : r)) ?? null)
              }
            />
          ))
        ))}
    </Stack>
  );
}

function AccountCard({ account: a, onChange }: { account: Account; onChange: (a: Account) => void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const [reason, setReason] = useState<Reason | "">("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stepUp = useStepUp();
  const requested = useRequested();
  const status = (s: string) => t(`account_status.${s}`, { defaultValue: s });

  const act = async (kind: "suspended" | "active" | "reverify") => {
    setNotice(null);
    if (!reason) return setError(t("admin_accounts.pick_reason"));
    setError(null);
    // Another administrator's status waits for a second admin (ADR 0041).
    const admin = kind !== "reverify" && a.role === "platform_admin";
    const token = admin ? await stepUp("admin_change") : null;
    if (admin && !token) return;
    setBusy(true);
    const path = { path: { account_id: a.account_id } };
    const { data, error, response } =
      kind === "reverify"
        ? await api.POST("/admin/accounts/{account_id}/reverify", { params: path, body: { reason } })
        : await api.POST("/admin/accounts/{account_id}/status", {
            params: { ...path, header: token ? { "X-Step-Up-Token": token } : {} },
            body: { status: kind, reason },
          });
    setBusy(false);
    if (response.status === 202) {
      setReason("");
      requested();
      return setNotice(t("changes.requested"));
    }
    if (!data) return setError(errorMessage(error, t("error.generic")));
    const next = data as Account;
    onChange(next);
    setReason("");
    setNotice(
      kind === "reverify"
        ? t("admin_accounts.reverified", { status: next.verification_status })
        : kind === "suspended"
          ? t("admin_accounts.suspended")
          : t("admin_accounts.reactivated"),
    );
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="account-card">
      <Stack gap="sm">
        <Group justify="space-between" gap="xs">
          <Title order={2} size="h5" ff="monospace" style={{ overflowWrap: "anywhere" }}>
            {a.account_id}
          </Title>
          <Group gap={4}>
            <Badge color={a.status === "active" ? "green" : "red"} data-testid="account-status">
              {status(a.status)}
            </Badge>
            {a.admin_hold && (
              <Badge color="red" variant="outline" data-testid="account-hold">
                {t("admin_accounts.hold_yes")}
              </Badge>
            )}
          </Group>
        </Group>
        <Table.ScrollContainer minWidth={280}>
          <Table verticalSpacing={4}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.role")}</Table.Th>
                <Table.Td>{a.role}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.email_masked")}</Table.Th>
                <Table.Td>{a.masked_email}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.phone_masked")}</Table.Th>
                <Table.Td>{a.masked_phone}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.verification")}</Table.Th>
                <Table.Td data-testid="account-verification">{a.verification_status}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.admin_hold")}</Table.Th>
                <Table.Td>{a.admin_hold ? t("admin_accounts.hold_yes") : t("admin_accounts.hold_no")}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.created")}</Table.Th>
                <Table.Td>{f.dateTime(a.created_at)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{t("admin_accounts.last_login")}</Table.Th>
                <Table.Td>{a.last_login_at ? f.dateTime(a.last_login_at) : t("admin_accounts.never")}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <NativeSelect
            label={t("admin_accounts.reason")}
            description={t("admin_accounts.reason_hint")}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value as Reason | "")}
            data={[
              { value: "", label: t("admin_reason.choose") },
              ...REASONS.map((r) => ({ value: r, label: t(`admin_reason.${r}`) })),
            ]}
          />
        </SimpleGrid>
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        <Group gap="xs">
          {a.status === "suspended" ? (
            <Button size="xs" variant="light" loading={busy} onClick={() => void act("active")}>
              {t("admin_accounts.reactivate")}
            </Button>
          ) : (
            <Button size="xs" color="red" variant="light" loading={busy} onClick={() => void act("suspended")}>
              {t("admin_accounts.suspend")}
            </Button>
          )}
          <Button size="xs" variant="default" loading={busy} onClick={() => void act("reverify")}>
            {t("admin_accounts.reverify")}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
